import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';

import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import TablePaginationActions from '../../../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../../../components/hooks/useTablePaginationInit';
import useSearchableSelect from '../../../../../components/hooks/useSearchableSelect';
import TableTimeStamps from '../../../../../components/utils/TableTimeStamps';
import SummaryTiles, { TILE_ACCENT } from '../../../../../components/utils/SummaryTiles';
import PartyHealthChip, { healthLabel } from '../../../../../components/utils/PartyHealthChip';
import ConfirmationModal from '../../../../../components/modals/ConfirmationModal';
import Copyable from '../../../../../components/utils/Copyable';
import TopBar from '../../../../../components/utils/TopBar';
import NA from '../../../../../components/utils/NA';

import CustomerDetailsModal from './CustomerDetailsModal';

import {
  GetCustomersBIZcoreRcv,
  GetCustomersAnalyticsBIZcoreRcv,
  RepairCustomersBIZcoreRcv
} from '../../../../../redux/actions/business/core/customersActions';
import {
  GetCompaniesDropdownBIZcore
} from '../../../../../redux/actions/business/core/companiesActions';

// The verdicts worth a filter chip, in the order they matter when triaging.
// "REPAIRABLE" is the union of everything the repair button would touch.
const HEALTH_FILTERS = [
  { value: "", label: "All", accent: TILE_ACCENT.info },
  { value: "REPAIRABLE", label: "Needs repair", accent: TILE_ACCENT.bad },
  { value: "FLAG_CONFLICT", label: "Both flags set", accent: TILE_ACCENT.bad },
  { value: "INTERNATIONAL_NO_CONFIG", label: "International, no config", accent: TILE_ACCENT.bad },
  { value: "DOMESTIC_UNCHECKED", label: "Domestic (not verifiable)", accent: TILE_ACCENT.info },
  { value: "CONFIG_WITHOUT_FLAG", label: "Config with no flag", accent: TILE_ACCENT.warn },
  { value: "UNASSIGNED", label: "Unassigned", accent: TILE_ACCENT.neutral },
  { value: "OK", label: "Healthy", accent: TILE_ACCENT.good },
];

// Tiles mirror the analytics payload; every status is shown even at zero so the
// row does not reshuffle as data changes.
const TILE_ORDER = [
  { key: "OK", accent: TILE_ACCENT.good },
  { key: "FLAG_CONFLICT", accent: TILE_ACCENT.bad },
  { key: "INTERNATIONAL_NO_CONFIG", accent: TILE_ACCENT.bad },
  { key: "DOMESTIC_UNCHECKED", accent: TILE_ACCENT.info },
  { key: "CONFIG_WITHOUT_FLAG", accent: TILE_ACCENT.warn },
  { key: "UNASSIGNED", accent: TILE_ACCENT.neutral },
];

const CustomersPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.core"]);

  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [repairing, setRepairing] = useState(false);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");

  const [company, setCompany] = useState(null);
  const [healthFilter, setHealthFilter] = useState("");

  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [repairModalOpen, setRepairModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [customersList, setCustomersList] = useState([]);
  const [customersTotal, setCustomersTotal] = useState(0);
  const [analytics, setAnalytics] = useState(null);

  const [
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const {
    results: companyResults,
    loading: companySearchLoading,
    handleSearch: handleCompanySearch
  } = useSearchableSelect(
    GetCompaniesDropdownBIZcore,
    (state) => {
      const list = state["business.core"].companiesDropdownList;
      if (list?.records && Array.isArray(list.records)) {
        return list.records;
      }
      return [];
    },
    null,
    { limit: 10, formatQuery: (q) => `&search=${q}` }
  );

  const tableHeadingsList = [
    { label: "Customer", sort: "name" },
    { label: "Company", sort: null },
    { label: "Rail", sort: null },
    { label: "Health", sort: null },
    { label: "Configuration", sort: null },
    { label: "Contact", sort: "email" },
    { label: "Time stamps", sort: "created_at" },
    { label: "", sort: null },
  ];

  const handleSort = (value) => {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    setSort(value);
  };

  // Any filter change invalidates the current offset — reset to the first page
  // in the same update so only one fetch goes out.
  const applyFilter = (apply) => {
    handleChangePage(null, 0);
    apply();
  };

  useEffect(() => {
    handleGetCustomers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection, company, healthFilter]);

  useEffect(() => {
    handleGetAnalytics();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  useEffect(() => {
    try {
      setCustomersList(state.customersList.records || []);
      setCustomersTotal(state.customersList.total || 0);
    } catch (err) {}
    try {
      setAnalytics(state.customersAnalytics || null);
    } catch (err) {}
  }, [state]);

  async function handleGetCustomers() {
    let query = "";
    if (search) query += `&search=${encodeURIComponent(search)}`;
    if (sort) query += `&ordering=${sortDirection === "desc" ? "-" : ""}${sort}`;
    dispatch(GetCustomersBIZcoreRcv(
      company?.company_id || null,
      healthFilter,
      page + 1,
      rowsPerPage,
      query,
      setLoading
    ));
  };

  async function handleGetAnalytics() {
    dispatch(GetCustomersAnalyticsBIZcoreRcv(company?.company_id || null, setAnalyticsLoading));
  };

  const handleRepair = () => {
    setRepairModalOpen(false);
    dispatch(RepairCustomersBIZcoreRcv(company?.company_id || null, setRepairing))
      .then(() => {
        handleGetCustomers();
        handleGetAnalytics();
      });
  };

  const summaryTiles = analytics
    ? [
        { label: "Customers in scope", value: analytics.total, accent: TILE_ACCENT.neutral },
        {
          label: "Need repair",
          value: analytics.repairable,
          accent: analytics.repairable ? TILE_ACCENT.bad : TILE_ACCENT.good,
          onClick: () => applyFilter(() => setHealthFilter("REPAIRABLE")),
        },
        ...TILE_ORDER.map((tile) => ({
          label: healthLabel(tile.key),
          value: analytics.counts?.[tile.key] ?? 0,
          accent: tile.accent,
          onClick: () => applyFilter(() => setHealthFilter(tile.key)),
        })),
      ]
    : [];

  return (
    <Box>

      <CustomerDetailsModal
        open={detailsModalOpen}
        setOpen={setDetailsModalOpen}
        customer={selectedCustomer}
        />

      <ConfirmationModal
        open={repairModalOpen}
        setOpen={setRepairModalOpen}
        title={"Repair customer flags"}
        message={
          `This clears every rail flag that no longer matches the configuration on file${
            company ? ` for ${company.company_name}` : " across this entire environment"
          }. A customer flagged both domestic and international has both flags cleared; a customer flagged international with no rcv_customer_international row has that flag cleared. is_domestic is never cleared on its own — there is no domestic configuration table on the receive side to check it against. Flags backed by a real configuration are never touched.`
        }
        onConfirm={handleRepair}
        />

      <TopBar
        title={"Customers"}
        search={search}
        searchPlaceholder={"Search name, contact, email, party id"}
        onSearchChange={(e) => {
          applyFilter(() => setSearch(e.target.value));
        }}
        button1Text={repairing ? "Repairing…" : "Repair flags"}
        button1Disabled={repairing || analyticsLoading}
        button1OnClick={() => setRepairModalOpen(true)}
        />

      <Box className='flexCenter_Row' gap={"15px"} mb={"14px"}>
        <Autocomplete
          sx={{ minWidth: "320px" }}
          size='small'
          options={Array.isArray(companyResults) ? companyResults : []}
          loading={companySearchLoading}
          value={company}
          onInputChange={(e, value, reason) => {
            if (reason === 'input') handleCompanySearch(value);
          }}
          onChange={(e, value) => applyFilter(() => setCompany(value || null))}
          getOptionLabel={(option) =>
            typeof option === 'string' ? option : (option.company_name || option.company_id || "NA")
          }
          isOptionEqualToValue={(option, value) => option.company_id === value?.company_id}
          renderInput={(params) => (
            <TextField {...params} placeholder='Filter by company — type to search' />
          )}
          />
        <Typography variant='caption' color={"grey"}>
          {
            company
            ? `Scoped to ${company.company_name}`
            : "All companies in this environment"
          }
        </Typography>
      </Box>

      {
        analyticsLoading && !analytics
        ?
        <Box height={"90px"} className="flexCenterCenterRow">
          <CircularProgress size={26} />
        </Box>
        :
        <SummaryTiles tiles={summaryTiles} />
      }

      <Box className='flexCenter_Row' gap={"10px"} mb={"12px"} flexWrap={"wrap"}>
        {
          HEALTH_FILTERS.map((filter) => (
            <Chip
              key={filter.value || "all"}
              size='small'
              label={filter.label}
              variant={healthFilter === filter.value ? 'filled' : 'outlined'}
              onClick={() => applyFilter(() => setHealthFilter(filter.value))}
              sx={{
                color: healthFilter === filter.value ? "#111" : filter.accent,
                background: healthFilter === filter.value ? filter.accent : "transparent",
                borderColor: `${filter.accent}60`,
                fontWeight: 600,
              }}
              />
          ))
        }
      </Box>

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          customersList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No customer found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort}
                        direction={sortDirection}
                        onClick={() => handleSort(heading.sort)}>
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                customersList.map((row, index) => {
                  const international = row.associated_international_customer || [];

                  return (
                    <TableRow key={index}>
                      <TableCell>
                        <Typography variant='body2'>
                          {row.name || <NA />}
                        </Typography>
                        <Typography variant='caption' color={"grey"}>
                          <Copyable>{row.customer_id}</Copyable>
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant='body2'>
                          {row.ref_company_id?.company_name || <NA />}
                        </Typography>
                        <Typography variant='caption' color={"grey"}>
                          {
                            row.ref_company_id?.company_id
                            ? <Copyable>{row.ref_company_id.company_id}</Copyable>
                            : <NA />
                          }
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Box className='flexCenter_Row' gap={"5px"} flexWrap={"wrap"}>
                          {row.is_domestic && <Chip size='small' label="Domestic" />}
                          {row.is_international && <Chip size='small' label="International" />}
                          {row.is_external && <Chip size='small' variant='outlined' label="External" />}
                          {
                            (!row.is_domestic && !row.is_international)
                            && <Typography variant='caption' color={"grey"}>None</Typography>
                          }
                        </Box>
                      </TableCell>

                      <TableCell>
                        <PartyHealthChip health={row.health} />
                      </TableCell>

                      <TableCell>
                        <Tooltip title={"International configuration · rcv_customer_international"}>
                          <Typography
                            variant='caption'
                            display={"block"}
                            sx={{ color: international.length ? TILE_ACCENT.good : "grey" }}>
                            {international.length} international row{international.length === 1 ? "" : "s"}
                          </Typography>
                        </Tooltip>
                        <Tooltip title={"The receive side has no domestic configuration table"}>
                          <Typography variant='caption' display={"block"} color={"grey"}>
                            no domestic table
                          </Typography>
                        </Tooltip>
                      </TableCell>

                      <TableCell>
                        <Typography variant='caption' display={"block"}>
                          {row.email || <NA />}
                        </Typography>
                        <Typography variant='caption' color={"grey"}>
                          {[row.phone_country_code, row.phone].filter(Boolean).join(" ") || <NA />}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <TableTimeStamps
                          createdAt={row.created_at}
                          updatedAt={row.updated_at}
                          />
                      </TableCell>

                      <TableCell>
                        <Tooltip title={"Full customer and configuration details"}>
                          <IconButton
                            size='small'
                            onClick={() => {
                              setSelectedCustomer(row);
                              setDetailsModalOpen(true);
                            }}>
                            <VisibilityOutlinedIcon fontSize='small' color='primary' />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  )
                })
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  colSpan={10}
                  count={customersTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: { 'aria-label': 'rows per page' },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                  />
              </TableRow>
            </TableFooter>
          </Table>
      }
    </Box>
  )
}

export default CustomersPage;
